import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaBan,
  FaBuilding,
  FaChevronDown,
  FaEdit,
  FaFileExport,
  FaEllipsisV,
  FaPlus,
  FaTimes,
  FaTrash,
  FaUnlock,
  FaStore,
  FaUser,
  FaEnvelope,
  FaGlobe,
  FaLayerGroup,
  FaBarcode,
} from "react-icons/fa";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import OrganizationDetails from "./OrganizationDetails";
import { useNavigate } from "react-router-dom";
import Pagination from "../components/Pagination";
import {
  deleteOrganization,
  disableOrganization,
  enableOrganization,
  getAllOrganizations,
  onboardOrganization,
  updateOrganization,
} from "../api/api";
import { getUserData } from "../utils/authStorage";
import { useToast } from "../context/ToastContext";
import "../styles/UserManagement.css";
import "../styles/OrganizationList.css";

/* Demo dataset used when API has no data or is unreachable */
const DUMMY_ORGANIZATIONS = [
  {
    orgId: 1001,
    orgName: "Apex Financial Group",
    username: "apex_fin",
    contact: "+1 202 555 0123",
    email: "ops@apexfin.com",
    industrySector: "Fintech Services",
    createdAt: "2026-05-28T10:12:00Z",
    isDisabled: false,
    totalMerchants: 1842,
    totalVolume: 9284000,
  },
  {
    orgId: 1002,
    orgName: "Northstar Logistics",
    username: "northstar_ops",
    contact: "+1 415 555 0148",
    email: "support@northstar.io",
    industrySector: "Energy Logistics",
    createdAt: "2026-05-17T08:45:00Z",
    orgStatus: "suspended",
    totalMerchants: 624,
    totalVolume: 2410000,
  },
  {
    orgId: 1003,
    orgName: "Mercury Retail Holdings",
    username: "mercury_rh",
    contact: "+44 20 7946 0958",
    email: "hello@mercuryrh.co.uk",
    industrySector: "Retail Commerce",
    createdAt: "2026-05-07T13:30:00Z",
    isDisabled: false,
    totalMerchants: 2104,
    totalVolume: 17480000,
  },
  {
    orgId: 1004,
    orgName: "Atlas Digital Assets",
    username: "atlas_chain",
    contact: "+1 917 555 0199",
    email: "ops@atlasda.com",
    industrySector: "Digital Assets",
    createdAt: "2026-04-26T09:18:00Z",
    isDisabled: true,
    totalMerchants: 318,
    totalVolume: 980000,
  },
  {
    orgId: 1005,
    orgName: "Bluebird Payments",
    username: "bluebird_pay",
    contact: "+1 303 555 0163",
    email: "admin@bluebirdpay.com",
    industrySector: "Fintech Services",
    createdAt: "2026-04-11T15:04:00Z",
    isDisabled: false,
    totalMerchants: 742,
    totalVolume: 5340000,
  },
  {
    orgId: 1006,
    orgName: "Helix Supply Chain",
    username: "helix_chain",
    contact: "+61 2 9374 4000",
    email: "contact@helixsc.au",
    industrySector: "Energy Logistics",
    createdAt: "2026-03-30T11:27:00Z",
    orgStatus: "suspended",
    totalMerchants: 289,
    totalVolume: 1620000,
  },
  {
    orgId: 1007,
    orgName: "Canyon Commerce Group",
    username: "canyon_cg",
    contact: "+1 602 555 0184",
    email: "ops@canyoncg.com",
    industrySector: "Retail Commerce",
    createdAt: "2026-03-12T10:00:00Z",
    isDisabled: false,
    totalMerchants: 1158,
    totalVolume: 8240000,
  },
  {
    orgId: 1008,
    orgName: "Orbit Digital Custody",
    username: "orbit_dc",
    contact: "+1 512 555 0110",
    email: "security@orbitdc.com",
    industrySector: "Digital Assets",
    createdAt: "2026-02-20T09:42:00Z",
    isDisabled: false,
    totalMerchants: 204,
    totalVolume: 2140000,
  },
  {
    orgId: 1009,
    orgName: "Sierra MicroPay",
    username: "sierra_micro",
    contact: "+34 91 555 0192",
    email: "ops@sierramp.es",
    industrySector: "Fintech Services",
    createdAt: "2026-02-05T13:12:00Z",
    isDisabled: false,
    totalMerchants: 382,
    totalVolume: 1830000,
  },
  {
    orgId: 1010,
    orgName: "Verde Retail Networks",
    username: "verde_rn",
    contact: "+55 11 5555 0102",
    email: "contact@verdern.br",
    industrySector: "Retail Commerce",
    createdAt: "2026-01-24T16:44:00Z",
    isDisabled: false,
    totalMerchants: 1440,
    totalVolume: 7320000,
  },
  {
    orgId: 1011,
    orgName: "Kepler Energy Trade",
    username: "kepler_et",
    contact: "+49 30 5555 0111",
    email: "ops@keplertrade.de",
    industrySector: "Energy Logistics",
    createdAt: "2025-12-30T12:05:00Z",
    isDisabled: true,
    totalMerchants: 214,
    totalVolume: 980000,
  },
  {
    orgId: 1012,
    orgName: "Nova Chain Vault",
    username: "nova_vault",
    contact: "+81 3 5555 0101",
    email: "admin@novavault.jp",
    industrySector: "Digital Assets",
    createdAt: "2025-12-05T08:26:00Z",
    isDisabled: false,
    totalMerchants: 96,
    totalVolume: 640000,
  },
];

const PAGE_SIZE = 7;

const SECTOR_OPTIONS = [
  { label: "All Industries", value: "all" },
  { label: "Fintech Services", value: "fintech_services" },
  { label: "Energy Logistics", value: "energy_logistics" },
  { label: "Retail Commerce", value: "retail_commerce" },
  { label: "Digital Assets", value: "digital_assets" },
];

const STATUS_OPTIONS = [
  { label: "Any Status", value: "all" },
  { label: "Active", value: "active" },
  { label: "Suspended", value: "suspended" },
  { label: "Blocked", value: "blocked" },
];

const SORT_OPTIONS = [
  { label: "Last 30 Days", value: "recent" },
  { label: "Oldest First", value: "oldest" },
  { label: "A-Z", value: "name" },
];

const STATUS_META = {
  active: { label: "ACTIVE" },
  suspended: { label: "SUSPENDED" },
  blocked: { label: "BLOCKED" },
};

const normalizeOptionValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const hashString = (value) =>
  String(value || "")
    .split("")
    .reduce((acc, char) => ((acc << 5) - acc + char.charCodeAt(0)) | 0, 0);

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCompactNumber = (value) =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: value >= 1000 ? 1 : 0,
  }).format(value);

const formatCurrencyCompact = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);

const parseRegistrationDate = (value, hash) => {
  const parsed = value ? new Date(value) : null;
  const valid = parsed && !Number.isNaN(parsed.getTime());
  const fallback = new Date(Date.now() - ((Math.abs(hash) % 45) + 3) * 86400000);
  const date = valid ? parsed : fallback;

  return {
    value: date.getTime(),
    label: date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
  };
};

const inferSector = (organization, hash) => {
  const explicit = organization.industrySector || organization.sector || organization.businessType;
  if (explicit) {
    const normalized = normalizeOptionValue(explicit);
    const matched = SECTOR_OPTIONS.find((option) => option.value === normalized);
    return {
      label: matched?.label || explicit,
      value: matched?.value || normalized,
    };
  }

  const fallback = SECTOR_OPTIONS[(Math.abs(hash) % (SECTOR_OPTIONS.length - 1)) + 1];
  return fallback;
};

const inferStatusKey = (organization) => {
  if (typeof organization.isDisabled === "boolean") {
    return organization.isDisabled ? "blocked" : "active";
  }

  const rawStatus = String(organization.status || organization.orgStatus || "").toLowerCase();
  if (rawStatus.includes("block") || rawStatus.includes("disable")) return "blocked";
  if (rawStatus.includes("suspend") || rawStatus.includes("review") || rawStatus.includes("pending")) return "suspended";
  return "active";
};

const inferHealth = (organization, statusKey, hash) => {
  const directScore = toNumber(organization.healthScore || organization.health);

  if (directScore > 0) {
    if (directScore >= 80) return { score: directScore, tone: "stable", label: `${directScore}% Stable` };
    if (directScore >= 45) return { score: directScore, tone: "warning", label: `${directScore}% Warning` };
    return { score: directScore, tone: "critical", label: `${directScore}% Critical` };
  }

  if (statusKey === "blocked") {
    const score = 12 + (Math.abs(hash) % 10);
    return { score, tone: "critical", label: `${score}% Critical` };
  }

  if (statusKey === "suspended") {
    const score = 52 + (Math.abs(hash) % 18);
    return { score, tone: "warning", label: `${score}% Warning` };
  }

  const score = 84 + (Math.abs(hash) % 14);
  return { score, tone: "stable", label: `${score}% Stable` };
};

const buildOrganizationViewModel = (organization, index) => {
  const id = organization.orgId || organization._id || `org-${index + 1}`;
  const hash = hashString(`${organization.orgName || organization.username || id}-${index}`);
  const sector = inferSector(organization, hash);
  const statusKey = inferStatusKey(organization);
  const health = inferHealth(organization, statusKey, hash);
  const registration = parseRegistrationDate(
    organization.registrationDate || organization.createdAt || organization.updatedAt,
    hash
  );
  const merchants = toNumber(organization.totalMerchants || organization.merchantCount || organization.merchants) || ((Math.abs(hash) % 2800) + 12);
  const volume = toNumber(organization.totalVolume || organization.transactionVolume || organization.volume) || merchants * ((Math.abs(hash) % 2100) + 180);
  const displayName = organization.orgName || organization.username || `Organization ${index + 1}`;

  return {
    raw: organization,
    orgId: id,
    orgName: displayName,
    initials: String(displayName).slice(0, 1).toUpperCase(),
    sectorLabel: sector.label,
    sectorValue: sector.value,
    statusKey,
    statusLabel: STATUS_META[statusKey]?.label || "ACTIVE",
    healthLabel: health.label,
    healthScore: Math.max(0, Math.min(100, health.score)),
    healthTone: health.tone,
    volumeRaw: volume,
    volumeText: formatCurrencyCompact(volume),
    merchantsRaw: merchants,
    merchantsText: formatCompactNumber(merchants),
    registrationLabel: registration.label,
    registrationValue: registration.value,
  };
};

const OrganizationList = ({ isSidebarOpen }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [isDemo, setIsDemo] = useState(false);
  const [sectorFilter, setSectorFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [page, setPage] = useState(0);
  const [sectorMenuOpen, setSectorMenuOpen] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openActionId, setOpenActionId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerOrg, setDrawerOrg] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [feeTypeMenuOpen, setFeeTypeMenuOpen] = useState(false);
  const [editFeeTypeMenuOpen, setEditFeeTypeMenuOpen] = useState(false);
  const feeTypeMenuRef = useRef(null);
  const editFeeTypeMenuRef = useRef(null);
  const { showSuccess, showError, showInfo } = useToast();
  const sectorMenuRef = React.useRef(null);
  const statusMenuRef = React.useRef(null);
  const sortMenuRef = React.useRef(null);
  const actionsMenuRef = React.useRef(null);

  const userData = getUserData();
  const user = userData?.user || userData?.data || userData || {};
  const userType = String(user?.user_type || user?.type || "").toLowerCase();
  const userRoles = Array.isArray(user?.roles) ? user.roles.flat() : [];
  const isAdmin = userType === "admin" || userRoles.includes("super_admin");
  const isViewOnly = userRoles.includes("view-only");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getAllOrganizations();
        if (Array.isArray(res?.data) && res.data.length > 0) {
          setOrganizations(res.data);
          setIsDemo(false);
        } else {
          setOrganizations(DUMMY_ORGANIZATIONS);
          setIsDemo(true);
        }
      } catch (err) {
        console.error("Error fetching organizations:", err);
        setOrganizations(DUMMY_ORGANIZATIONS);
        setIsDemo(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [sectorFilter, statusFilter, sortBy, organizations.length]);

  useEffect(() => {
    const handleDocMouseDown = (event) => {
      if (sectorMenuRef.current && !sectorMenuRef.current.contains(event.target)) {
        setSectorMenuOpen(false);
      }
      if (statusMenuRef.current && !statusMenuRef.current.contains(event.target)) {
        setStatusMenuOpen(false);
      }
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target)) {
        setSortMenuOpen(false);
      }
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target)) {
        setOpenActionId(null);
      }
      if (feeTypeMenuRef.current && !feeTypeMenuRef.current.contains(event.target)) {
        setFeeTypeMenuOpen(false);
      }
      if (editFeeTypeMenuRef.current && !editFeeTypeMenuRef.current.contains(event.target)) {
        setEditFeeTypeMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocMouseDown);
    return () => document.removeEventListener("mousedown", handleDocMouseDown);
  }, []);

  useEffect(() => {
    if (!sectorMenuOpen && !statusMenuOpen && !sortMenuOpen && !deleteTarget && !drawerOpen && !createDialogOpen && !editDialogOpen) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setSectorMenuOpen(false);
        setStatusMenuOpen(false);
        setSortMenuOpen(false);
        setDeleteTarget(null);
        setDrawerOpen(false);
        setCreateDialogOpen(false);
        setEditDialogOpen(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [sectorMenuOpen, statusMenuOpen, sortMenuOpen, deleteTarget, openActionId, drawerOpen, createDialogOpen, editDialogOpen]);

  const refreshOrganizations = async () => {
    if (isDemo) return; // local demo data is already source of truth
    try {
      const res = await getAllOrganizations();
      if (Array.isArray(res?.data)) {
        setOrganizations(res.data);
      }
    } catch (err) {
      console.error("Error refreshing organizations:", err);
    }
  };

  const handleDeleteOrganization = async (id) => {
    if (isDemo) {
      setOrganizations((prev) => prev.filter((o) => (o.orgId || o._id) !== id));
      showSuccess("Deleted", "Organization deleted successfully");
      return;
    }
    try {
      const res = await deleteOrganization(id);
      if (res?.isSuccess) {
        showSuccess("Deleted", "Organization deleted successfully");
        await refreshOrganizations();
      } else {
        showError("Error", res?.message || "Failed to delete organization", 3000);
      }
    } catch {
      showError("Error", "Something went wrong while deleting the organization", 3000);
    }
  };

  const handleEnableOrganization = async (id) => {
    if (isDemo) {
      setOrganizations((prev) => prev.map((o) => (o.orgId === id || o._id === id ? { ...o, isDisabled: false, orgStatus: undefined } : o)));
      showSuccess("Enabled", "Organization enabled successfully");
      return;
    }
    try {
      const res = await enableOrganization(id);
      if (res?.isSuccess) {
        showSuccess("Enabled", "Organization enabled successfully");
        await refreshOrganizations();
      } else {
        showError("Error", res?.message || "Failed to enable organization", 3000);
      }
    } catch {
      showError("Error", "Something went wrong while enabling the organization", 3000);
    }
  };

  const handleDisableOrganization = async (id) => {
    if (isDemo) {
      setOrganizations((prev) => prev.map((o) => (o.orgId === id || o._id === id ? { ...o, isDisabled: true } : o)));
      showSuccess("Disabled", "Organization disabled successfully");
      return;
    }
    try {
      const res = await disableOrganization(id);
      if (res?.isSuccess) {
        showSuccess("Disabled", "Organization disabled successfully");
        await refreshOrganizations();
      } else {
        showError("Error", res?.message || "Failed to disable organization", 3000);
      }
    } catch {
      showError("Error", "Something went wrong while disabling the organization", 3000);
    }
  };

  const organizationRows = useMemo(
    () => organizations.map((organization, index) => buildOrganizationViewModel(organization, index)),
    [organizations]
  );

  const filteredOrganizations = useMemo(() => {
    return organizationRows.filter((organization) => {
      if (sectorFilter !== "all" && organization.sectorValue !== sectorFilter) return false;
      if (statusFilter !== "all" && organization.statusKey !== statusFilter) return false;
      return true;
    });
  }, [organizationRows, sectorFilter, statusFilter]);

  const sortedOrganizations = useMemo(() => {
    const copy = [...filteredOrganizations];

    if (sortBy === "name") {
      return copy.sort((a, b) => a.orgName.localeCompare(b.orgName));
    }

    if (sortBy === "oldest") {
      return copy.sort((a, b) => a.registrationValue - b.registrationValue);
    }

    return copy.sort((a, b) => b.registrationValue - a.registrationValue);
  }, [filteredOrganizations, sortBy]);

  const total = sortedOrganizations.length;
  const pageOrganizations = sortedOrganizations.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const sectorLabel = SECTOR_OPTIONS.find((option) => option.value === sectorFilter)?.label || "All Industries";
  const statusLabel = STATUS_OPTIONS.find((option) => option.value === statusFilter)?.label || "Any Status";
  const sortLabel = SORT_OPTIONS.find((option) => option.value === sortBy)?.label || "Last 30 Days";

  const clearFilters = () => {
    setSectorFilter("all");
    setStatusFilter("all");
    setSortBy("recent");
  };

  const handleExport = () => {
    if (!sortedOrganizations.length) {
      showInfo("Export", "There are no organizations to export", 2200);
      return;
    }

    const lines = [
      ["Organization", "Sector", "Health", "Volume", "Merchants", "Status", "Registration Date"].join(","),
      ...sortedOrganizations.map((organization) => (
        [
          organization.orgName,
          organization.sectorLabel,
          organization.healthLabel,
          organization.volumeText,
          organization.merchantsText,
          organization.statusLabel,
          organization.registrationLabel,
        ]
          .map((value) => `"${String(value).replace(/"/g, '""')}"`)
          .join(",")
      )),
    ];

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "organization-ecosystem.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  /* ── Org name with icon ── */
  const renderOrganizationCell = (organization) => (
    <div className="oe-orgcell">
      <span className={`oe-orgcell__avatar oe-orgcell__avatar--${organization.statusKey}`}>
        {organization.initials || <FaBuilding />}
      </span>
      <div className="oe-orgcell__meta">
        <div className="oe-orgcell__name">{organization.orgName}</div>
        <div className="oe-orgcell__sub">{organization.sectorLabel}</div>
      </div>
    </div>
  );

  /* ── Status badge ── */
  const renderStatusBadge = (organization) => (
    <span className={`oe-status oe-status--${organization.statusKey}`}>{organization.statusLabel}</span>
  );

  /* ── Action buttons ── */
  const renderActions = (organization) => (
    <div className="oe-actions" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="oe-detailsbtn"
        onClick={() =>
          navigate("/dashboard/OrganizationDetails", {
            state: { orgId: organization.orgId, name: organization.orgName },
          })
        }
      >
        View Details
      </button>

      {isAdmin && !isViewOnly ? (
        <div
          className="oe-actions__more"
          ref={openActionId === organization.orgId ? actionsMenuRef : null}
        >
          <button
            type="button"
            className={openActionId === organization.orgId ? "oe-iconbtn open" : "oe-iconbtn"}
            title="More actions"
            aria-haspopup="menu"
            aria-expanded={openActionId === organization.orgId}
            onClick={(e) => {
              e.stopPropagation();
              setSectorMenuOpen(false);
              setStatusMenuOpen(false);
              setSortMenuOpen(false);
              setOpenActionId((cur) => (cur === organization.orgId ? null : organization.orgId));
            }}
          >
            <FaEllipsisV />
          </button>

          {openActionId === organization.orgId ? (
            <div className="um-sortmenu oe-actions__menu" role="menu">
              <button
                type="button"
                role="menuitem"
                className="um-sortmenu__item"
                onClick={() => {
                  setEditOrg(organization.raw);
                  setEditDialogOpen(true);
                  setOpenActionId(null);
                }}
              >
                Edit Organization
              </button>

              {organization.statusKey === "blocked" ? (
                <button
                  type="button"
                  role="menuitem"
                  className="um-sortmenu__item"
                  onClick={() => {
                    setOpenActionId(null);
                    handleEnableOrganization(organization.orgId);
                  }}
                >
                  Enable Organization
                </button>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  className="um-sortmenu__item"
                  onClick={() => {
                    setOpenActionId(null);
                    handleDisableOrganization(organization.orgId);
                  }}
                >
                  Disable Organization
                </button>
              )}

              <button
                type="button"
                role="menuitem"
                className="um-sortmenu__item"
                onClick={() => {
                  setOpenActionId(null);
                  setDeleteTarget(organization);
                }}
              >
                Delete Organization
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  /* ── Table header ── */
  if (loading) {
    return (
      <div className={`oe-page ${isSidebarOpen ? "shifted" : ""}`}>
        <div className="oe-loading">Loading organizations…</div>
      </div>
    );
  }

  return (
    <div className={`oe-page ${isSidebarOpen ? "shifted" : ""}`}>
      <div className="oe-header">
        <div>
          <h2 className="oe-title">Organization Ecosystem</h2>
          <div className="oe-subtitle">Observe and optimize your institutional partners and merchant networks.</div>
        </div>

        <div className="oe-headeractions">
          <button type="button" className="oe-secondarybtn" onClick={handleExport}>
            <FaFileExport />
            Export
          </button>

          {isAdmin && !isViewOnly ? (
            <button
              type="button"
              className="oe-primarybtn"
              onClick={() => setCreateDialogOpen(true)}
            >
              <FaPlus />
              Create Organization
            </button>
          ) : null}
        </div>
      </div>

      <div className="oe-toolbar">
        <div className="oe-filter" ref={sectorMenuRef}>
          <div className="oe-filter__label">Industry Sector</div>
          <button
            type="button"
            className={sectorMenuOpen ? "um-sortpill oe-filter__button open" : "um-sortpill oe-filter__button"}
            onClick={() => {
              setSectorMenuOpen((open) => !open);
              setStatusMenuOpen(false);
              setSortMenuOpen(false);
            }}
            aria-haspopup="menu"
            aria-expanded={sectorMenuOpen}
          >
            <span className="oe-filter__value">{sectorLabel}</span>
            <FaChevronDown className={sectorMenuOpen ? "um-sortpill__icon open" : "um-sortpill__icon"} />
          </button>
          {sectorMenuOpen ? (
            <div className="um-sortmenu oe-filter__menu" role="menu">
              {SECTOR_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="menuitem"
                  className={option.value === sectorFilter ? "um-sortmenu__item active" : "um-sortmenu__item"}
                  onClick={() => {
                    setSectorFilter(option.value);
                    setSectorMenuOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="oe-filter" ref={statusMenuRef}>
          <div className="oe-filter__label">Status</div>
          <button
            type="button"
            className={statusMenuOpen ? "um-sortpill oe-filter__button open" : "um-sortpill oe-filter__button"}
            onClick={() => {
              setStatusMenuOpen((open) => !open);
              setSectorMenuOpen(false);
              setSortMenuOpen(false);
            }}
            aria-haspopup="menu"
            aria-expanded={statusMenuOpen}
          >
            <span className="oe-filter__value">{statusLabel}</span>
            <FaChevronDown className={statusMenuOpen ? "um-sortpill__icon open" : "um-sortpill__icon"} />
          </button>
          {statusMenuOpen ? (
            <div className="um-sortmenu oe-filter__menu" role="menu">
              {STATUS_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="menuitem"
                  className={option.value === statusFilter ? "um-sortmenu__item active" : "um-sortmenu__item"}
                  onClick={() => {
                    setStatusFilter(option.value);
                    setStatusMenuOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="oe-filter" ref={sortMenuRef}>
          <div className="oe-filter__label">Registration Date</div>
          <button
            type="button"
            className={sortMenuOpen ? "um-sortpill oe-filter__button open" : "um-sortpill oe-filter__button"}
            onClick={() => {
              setSortMenuOpen((open) => !open);
              setSectorMenuOpen(false);
              setStatusMenuOpen(false);
            }}
            aria-haspopup="menu"
            aria-expanded={sortMenuOpen}
          >
            <span className="oe-filter__value">{sortLabel}</span>
            <FaChevronDown className={sortMenuOpen ? "um-sortpill__icon open" : "um-sortpill__icon"} />
          </button>
          {sortMenuOpen ? (
            <div className="um-sortmenu oe-filter__menu" role="menu">
              {SORT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="menuitem"
                  className={option.value === sortBy ? "um-sortmenu__item active" : "um-sortmenu__item"}
                  onClick={() => {
                    setSortBy(option.value);
                    setSortMenuOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <button type="button" className="oe-clearbtn" onClick={clearFilters}>
          Clear Filters
        </button>
      </div>

      <section className="oe-panel">
        <div className="oe-tableviewport">
          <div className="oe-grid oe-tablehead">
            <div>Organization &amp; Sector</div>
            <div>Health</div>
            <div>Vol. (30D)</div>
            <div>Merchants</div>
            <div>Status</div>
            <div>Actions</div>
          </div>

          <div className="oe-tablebody">
            {pageOrganizations.length ? (
              pageOrganizations.map((organization) => (
                <div key={organization.orgId} className="oe-grid oe-rowcard" onClick={() => { setDrawerOrg(organization); setDrawerOpen(true); }}>
                  <div>{renderOrganizationCell(organization)}</div>

                  <div className="oe-healthcell">
                    <div className={`oe-healthtext oe-healthtext--${organization.healthTone}`}>{organization.healthLabel}</div>
                    <div className="oe-healthbar">
                      <div
                        className={`oe-healthbar__fill oe-healthbar__fill--${organization.healthTone}`}
                        style={{ width: `${organization.healthScore}%` }}
                      />
                    </div>
                  </div>

                  <div className="oe-metriccell">
                    <div className="oe-metriccell__value">{organization.volumeText}</div>
                    <div className="oe-metriccell__sub">{organization.registrationLabel}</div>
                  </div>

                  <div className="oe-metriccell">
                    <div className="oe-metriccell__value">{organization.merchantsText}</div>
                    <div className="oe-metriccell__sub">Org ID {organization.orgId}</div>
                  </div>

                  <div>{renderStatusBadge(organization)}</div>

                  <div>{renderActions(organization)}</div>
                </div>
              ))
            ) : (
              <div className="oe-empty">
                <div className="oe-empty__title">No organizations found</div>
                <div className="oe-empty__subtitle">Try changing your sector, status, or date filters.</div>
              </div>
            )}
          </div>
        </div>

        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
          itemLabel="organizations"
        />

        {drawerOpen ? (
          <button
            type="button"
            className="oe-drawer-scrim"
            aria-label="Close drawer"
            onClick={() => setDrawerOpen(false)}
          />
        ) : null}

        <aside className={drawerOpen ? "oe-drawer open" : "oe-drawer"} aria-hidden={!drawerOpen}>
        {drawerOrg ? (
          <>
            <div className="oe-drawer__header">
              <div className="oe-drawer__kicker">Organization Detail</div>
              <button type="button" className="oe-drawer__close" onClick={() => setDrawerOpen(false)} aria-label="Close drawer">
                <FaTimes />
              </button>
            </div>
            <div className="oe-drawer__user">
              <div className="oe-drawer__avatar">{String(drawerOrg.orgName).slice(0,1)}</div>
              <div>
                <div className="oe-drawer__name">{drawerOrg.orgName}</div>
                <div className="oe-drawer__meta">
                  <span className="oe-drawer__orgid">ID {drawerOrg.orgId}</span>
                </div>
              </div>
            </div>
            <div className="oe-drawer__body">
              <OrganizationDetails
                orgId={drawerOrg.orgId}
                orgName={drawerOrg.orgName}
                isSidebarOpen={isSidebarOpen}
                mode="drawer"
              />
            </div>
            <div className="oe-drawer__footer">
              <button
                type="button"
                className="oe-drawer__btn oe-drawer__btn--ghost"
                onClick={() => {
                  setEditOrg(drawerOrg.raw);
                  setDrawerOpen(false);
                  setEditDialogOpen(true);
                }}
              >
                <FaEdit /> Edit Entity
              </button>
              <button type="button" className="oe-drawer__btn oe-drawer__btn--danger" onClick={() => setDrawerOpen(false)}>
                <FaBan /> Block Access
              </button>
            </div>
          </>
        ) : null}
        </aside>
      </section>

      {deleteTarget ? (
        <div className="um-modaloverlay" role="dialog" aria-modal="true" aria-label="Delete Organization">
          <button type="button" className="um-modaloverlay__scrim" aria-label="Close delete confirmation" onClick={() => setDeleteTarget(null)} />
          <div className="um-confirmdialog">
            <div className="um-confirmdialog__header">
              <h3 className="um-confirmdialog__title">Delete Organization</h3>
              <button type="button" className="um-adddialog__close" onClick={() => setDeleteTarget(null)}>
                <FaTimes />
              </button>
            </div>
            <div className="um-confirmdialog__body">
              <p className="um-confirmdialog__text">
                Are you sure you want to delete <strong>{deleteTarget.orgName}</strong>?
              </p>
              <p className="um-confirmdialog__subtext">
                This action will permanently remove the organization from the ecosystem list.
              </p>
            </div>
            <div className="um-confirmdialog__footer">
              <button type="button" className="um-adddialog__back" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button
                type="button"
                className="um-confirmdialog__delete"
                onClick={async () => {
                  const targetId = deleteTarget.orgId;
                  setDeleteTarget(null);
                  await handleDeleteOrganization(targetId);
                }}
              >
                Delete Organization
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {createDialogOpen ? (
        <div className="um-modaloverlay" role="dialog" aria-modal="true" aria-label="Create Organization">
          <button type="button" className="um-modaloverlay__scrim" aria-label="Close create organization dialog" onClick={() => setCreateDialogOpen(false)} />
          <Formik
            initialValues={{
              orgName: "",
              username: "",
              contact: "92",
              email: "",
              website: "",
              feeType: "",
              feeValue: "",
            }}
            validationSchema={Yup.object().shape({
              orgName: Yup.string().required("Organization name is required"),
              username: Yup.string().min(3, "Minimum 3 characters").required("Username is required"),
              contact: Yup.string().matches(/^[0-9]{10,15}$/, "Invalid contact number").required("Contact is required"),
              email: Yup.string().email("Invalid email").required("Email is required"),
              website: Yup.string().url("Invalid website URL").required("Website is required"),
              feeType: Yup.string().oneOf(["F", "P", "S"], "Invalid fee type").required("Fee type is required"),
              feeValue: Yup.string().required("Fee value is required"),
            })}
            onSubmit={async (values, { resetForm, setSubmitting }) => {
              try {
                const res = await onboardOrganization(values);
                if (res?.isSuccess) {
                  showSuccess("Created", res?.message || "Organization created successfully");
                  resetForm();
                  setCreateDialogOpen(false);
                  await refreshOrganizations();
                } else {
                  showError("Error", res?.message || "Failed to create organization", 3000);
                }
              } catch (err) {
                console.error("Organization Error:", err);
                showError("Error", "Something went wrong!", 3000);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, errors, touched, setFieldValue, isSubmitting }) => (
              <Form className="um-adddialog-shell um-adddialog-shell--overlay">
                <div className="um-adddialog">
                  <div className="um-adddialog__header">
                    <div>
                      <h2 className="um-adddialog__title">Create Organization</h2>
                      <div className="um-adddialog__subtitle">Onboard a new institutional partner into the ecosystem.</div>
                    </div>
                    <button type="button" className="um-adddialog__close" onClick={() => setCreateDialogOpen(false)}>
                      <FaTimes />
                    </button>
                  </div>

                  <div className="um-adddialog__body">
                    <div className="um-adddialog__grid">
                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="org-name">Organization Name</label>
                        <div className="um-forminputwrap">
                          <FaStore className="um-forminputwrap__icon" />
                          <Field id="org-name" name="orgName" type="text" className="um-adddialog__input" placeholder="e.g. Apex Financial Group" />
                        </div>
                        {touched.orgName && errors.orgName ? <div className="error-text">{errors.orgName}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="org-username">Username</label>
                        <div className="um-forminputwrap">
                          <FaUser className="um-forminputwrap__icon" />
                          <Field id="org-username" name="username" type="text" className="um-adddialog__input" placeholder="e.g. apex_fin" />
                        </div>
                        {touched.username && errors.username ? <div className="error-text">{errors.username}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label">Contact Number</label>
                        <PhoneInput
                          country="pk"
                          value={values.contact}
                          onChange={(value) => setFieldValue("contact", value.replace(/\D/g, ""))}
                          inputClass="um-adddialog__input"
                          countryCodeEditable={false}
                        />
                        {touched.contact && errors.contact ? <div className="error-text">{errors.contact}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="org-email">Email Address</label>
                        <div className="um-forminputwrap">
                          <FaEnvelope className="um-forminputwrap__icon" />
                          <Field id="org-email" name="email" type="email" className="um-adddialog__input" placeholder="ops@apexfin.com" />
                        </div>
                        {touched.email && errors.email ? <div className="error-text">{errors.email}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field um-adddialog__field--full">
                        <label className="um-adddialog__label" htmlFor="org-website">Website URL</label>
                        <div className="um-forminputwrap">
                          <FaGlobe className="um-forminputwrap__icon" />
                          <Field id="org-website" name="website" type="url" className="um-adddialog__input" placeholder="https://apexfin.com" />
                        </div>
                        {touched.website && errors.website ? <div className="error-text">{errors.website}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label">Fee Type</label>
                        <div className="um-formselectwrap" ref={feeTypeMenuRef}>
                          <button
                            type="button"
                            className={feeTypeMenuOpen ? "um-sortpill um-formselect open" : "um-sortpill um-formselect"}
                            onClick={() => setFeeTypeMenuOpen((open) => !open)}
                            aria-haspopup="menu"
                            aria-expanded={feeTypeMenuOpen}
                          >
                            <span className="um-formselect__value">
                              {values.feeType
                                ? { F: "Fixed Fee", P: "Percentage Fee", S: "Slab Based Fee" }[values.feeType]
                                : "Select Fee Type"}
                            </span>
                            <FaChevronDown className={feeTypeMenuOpen ? "um-sortpill__icon open" : "um-sortpill__icon"} />
                          </button>
                          {feeTypeMenuOpen ? (
                            <div className="um-sortmenu um-formselectmenu" role="menu">
                              {[
                                { label: "Fixed Fee", value: "F" },
                                { label: "Percentage Fee", value: "P" },
                                { label: "Slab Based Fee", value: "S" },
                              ].map((option) => (
                                <button
                                  key={option.value}
                                  type="button"
                                  role="menuitem"
                                  className={option.value === values.feeType ? "um-sortmenu__item active" : "um-sortmenu__item"}
                                  onClick={() => {
                                    setFieldValue("feeType", option.value);
                                    setFieldValue("feeValue", "");
                                    setFeeTypeMenuOpen(false);
                                  }}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        {touched.feeType && errors.feeType ? <div className="error-text">{errors.feeType}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="org-feevalue">Fee Value</label>
                        <div className="um-forminputwrap">
                          <FaBarcode className="um-forminputwrap__icon" />
                          <Field
                            id="org-feevalue"
                            name="feeValue"
                            type="text"
                            className="um-adddialog__input"
                            placeholder={
                              values.feeType === "F"
                                ? "Enter Fixed Amount"
                                : values.feeType === "P"
                                  ? "Enter Percentage (%)"
                                  : values.feeType === "S"
                                    ? "Enter Fee Profile ID"
                                    : "Enter Fee Value"
                            }
                            onInput={(e) => {
                              let value = e.target.value.replace(/[^0-9.]/g, "");
                              const parts = value.split(".");
                              if (parts.length > 2) value = parts[0] + "." + parts[1];
                              e.target.value = value;
                            }}
                          />
                        </div>
                        {touched.feeValue && errors.feeValue ? <div className="error-text">{errors.feeValue}</div> : <div className="error-text" />}
                      </div>
                    </div>
                  </div>

                  <div className="um-adddialog__footer">
                    <button type="button" className="um-adddialog__cancel" onClick={() => setCreateDialogOpen(false)}>Cancel</button>
                    <button type="submit" className="um-adddialog__submit" disabled={isSubmitting}>
                      {isSubmitting ? "Creating..." : "Create Organization"}
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      ) : null}
 // edit dialog
      {editDialogOpen && editOrg ? (
        <div className="um-modaloverlay" role="dialog" aria-modal="true" aria-label="Edit Organization">
          <button type="button" className="um-modaloverlay__scrim" aria-label="Close edit organization dialog" onClick={() => { setEditDialogOpen(false); setEditOrg(null); setEditFeeTypeMenuOpen(false); }} />
          <Formik
            initialValues={{
              orgName: editOrg.orgName || "",
              username: editOrg.username || "",
              contact: String(editOrg.contact || "92").replace(/\D/g, ""),
              email: editOrg.email || "",
              website: editOrg.website || "",
              feeType: editOrg.feeType || "",
              feeValue: editOrg.feeValue || "",
            }}
            validationSchema={Yup.object().shape({
              orgName: Yup.string().required("Organization name is required"),
              username: Yup.string().min(3, "Minimum 3 characters").required("Username is required"),
              contact: Yup.string().matches(/^[0-9]{10,15}$/, "Invalid contact number").required("Contact is required"),
              email: Yup.string().email("Invalid email").required("Email is required"),
              website: Yup.string().url("Invalid website URL").required("Website is required"),
              feeType: Yup.string().oneOf(["F", "P", "S"], "Invalid fee type").required("Fee type is required"),
              feeValue: Yup.string().required("Fee value is required"),
            })}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                const res = await updateOrganization(editOrg.orgId, values);
                if (res?.isSuccess) {
                  showSuccess("Updated", res?.message || "Organization updated successfully");
                  setEditDialogOpen(false);
                  setEditOrg(null);
                  await refreshOrganizations();
                } else {
                  showError("Error", res?.message || "Failed to update organization", 3000);
                }
              } catch (err) {
                console.error("Edit organization error:", err);
                showError("Error", "Something went wrong!", 3000);
              } finally {
                setSubmitting(false);
              }
            }}
            enableReinitialize
          >
            {({ values, errors, touched, setFieldValue, isSubmitting }) => (
              <Form className="um-adddialog-shell um-adddialog-shell--overlay">
                <div className="um-adddialog">
                  <div className="um-adddialog__header">
                    <div>
                      <h2 className="um-adddialog__title">Edit Organization</h2>
                      <div className="um-adddialog__subtitle">Update organization details.</div>
                    </div>
                    <button type="button" className="um-adddialog__close" onClick={() => { setEditDialogOpen(false); setEditOrg(null); setEditFeeTypeMenuOpen(false); }}>
                      <FaTimes />
                    </button>
                  </div>

                  <div className="um-adddialog__body">
                    <div className="um-adddialog__grid">
                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="edit-org-name">Organization Name</label>
                        <div className="um-forminputwrap">
                          <FaStore className="um-forminputwrap__icon" />
                          <Field id="edit-org-name" name="orgName" type="text" className="um-adddialog__input" placeholder="e.g. Apex Financial Group" />
                        </div>
                        {touched.orgName && errors.orgName ? <div className="error-text">{errors.orgName}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="edit-org-username">Username</label>
                        <div className="um-forminputwrap">
                          <FaUser className="um-forminputwrap__icon" />
                          <Field id="edit-org-username" name="username" type="text" className="um-adddialog__input" placeholder="e.g. apex_fin" />
                        </div>
                        {touched.username && errors.username ? <div className="error-text">{errors.username}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label">Contact Number</label>
                        <PhoneInput
                          country="pk"
                          value={values.contact}
                          onChange={(value) => setFieldValue("contact", value.replace(/\D/g, ""))}
                          inputClass="um-adddialog__input"
                          countryCodeEditable={false}
                        />
                        {touched.contact && errors.contact ? <div className="error-text">{errors.contact}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="edit-org-email">Email Address</label>
                        <div className="um-forminputwrap">
                          <FaEnvelope className="um-forminputwrap__icon" />
                          <Field id="edit-org-email" name="email" type="email" className="um-adddialog__input" placeholder="ops@apexfin.com" />
                        </div>
                        {touched.email && errors.email ? <div className="error-text">{errors.email}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field um-adddialog__field--full">
                        <label className="um-adddialog__label" htmlFor="edit-org-website">Website URL</label>
                        <div className="um-forminputwrap">
                          <FaGlobe className="um-forminputwrap__icon" />
                          <Field id="edit-org-website" name="website" type="url" className="um-adddialog__input" placeholder="https://apexfin.com" />
                        </div>
                        {touched.website && errors.website ? <div className="error-text">{errors.website}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label">Fee Type</label>
                        <div className="um-formselectwrap" ref={editFeeTypeMenuRef}>
                          <button
                            type="button"
                            className={editFeeTypeMenuOpen ? "um-sortpill um-formselect open" : "um-sortpill um-formselect"}
                            onClick={() => setEditFeeTypeMenuOpen((open) => !open)}
                            aria-haspopup="menu"
                            aria-expanded={editFeeTypeMenuOpen}
                          >
                            <span className="um-formselect__value">
                              {values.feeType
                                ? { F: "Fixed Fee", P: "Percentage Fee", S: "Slab Based Fee" }[values.feeType]
                                : "Select Fee Type"}
                            </span>
                            <FaChevronDown className={editFeeTypeMenuOpen ? "um-sortpill__icon open" : "um-sortpill__icon"} />
                          </button>
                          {editFeeTypeMenuOpen ? (
                            <div className="um-sortmenu um-formselectmenu" role="menu">
                              {[
                                { label: "Fixed Fee", value: "F" },
                                { label: "Percentage Fee", value: "P" },
                                { label: "Slab Based Fee", value: "S" },
                              ].map((option) => (
                                <button
                                  key={option.value}
                                  type="button"
                                  role="menuitem"
                                  className={option.value === values.feeType ? "um-sortmenu__item active" : "um-sortmenu__item"}
                                  onClick={() => {
                                    setFieldValue("feeType", option.value);
                                    setFieldValue("feeValue", "");
                                    setEditFeeTypeMenuOpen(false);
                                  }}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        {touched.feeType && errors.feeType ? <div className="error-text">{errors.feeType}</div> : <div className="error-text" />}
                      </div>

                      <div className="um-adddialog__field">
                        <label className="um-adddialog__label" htmlFor="edit-org-feevalue">Fee Value</label>
                        <div className="um-forminputwrap">
                          <FaBarcode className="um-forminputwrap__icon" />
                          <Field
                            id="edit-org-feevalue"
                            name="feeValue"
                            type="text"
                            className="um-adddialog__input"
                            placeholder={
                              values.feeType === "F"
                                ? "Enter Fixed Amount"
                                : values.feeType === "P"
                                  ? "Enter Percentage (%)"
                                  : values.feeType === "S"
                                    ? "Enter Fee Profile ID"
                                    : "Enter Fee Value"
                            }
                            onInput={(e) => {
                              let value = e.target.value.replace(/[^0-9.]/g, "");
                              const parts = value.split(".");
                              if (parts.length > 2) value = parts[0] + "." + parts[1];
                              e.target.value = value;
                            }}
                          />
                        </div>
                        {touched.feeValue && errors.feeValue ? <div className="error-text">{errors.feeValue}</div> : <div className="error-text" />}
                      </div>
                    </div>
                  </div>

                  <div className="um-adddialog__footer">
                    <button type="button" className="um-adddialog__cancel" onClick={() => { setEditDialogOpen(false); setEditOrg(null); setEditFeeTypeMenuOpen(false); }}>Cancel</button>
                    <button type="submit" className="um-adddialog__submit" disabled={isSubmitting}>
                      {isSubmitting ? "Updating..." : "Update Organization"}
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      ) : null}
    </div>
  );
};

export default OrganizationList;
